import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';

import { useUser } from '@/modules/auth/hooks/use-user';
import { savePendingCoupon } from '@/modules/orders/utils/pending-coupon';
import { enhancedToast } from '@/shared/utils/enhanced-toast';

import { getOptInOffer, recordPromptEvent, registerPushSubscription } from './api';
import {
  currentSubscriptionId,
  enablePush,
  isStandalone,
  platform,
  pushEnabled,
  pushErrorReason,
  pushState,
  syncPushIdentity,
} from './push-client';
import type { OptInGranted, PromptAction, PromptChannel, PushState } from './types';
import {
  deviceId,
  firstPromptView,
  isPushSnoozed,
  isSubscribedToStore,
  markSubscribedToStore,
  snoozePush,
} from './utils';

type Options = {
  slug: string;
  merchantId?: string | null;
  channel: PromptChannel;
  /** Momento contado para quem já tinha dito "Agora não" (ex.: a faixa do acompanhamento). */
  snoozedChannel?: PromptChannel;
  /** O convite está na tela (conta "viu" uma vez por aba e chave). */
  visible: boolean;
  /** Chave do "viu" (ex.: id do pedido). Padrão: a loja. */
  viewKey?: string;
};

/**
 * PRAT-43 — o que todo convite de push faz: aviso nosso antes do pedido de
 * permissão do navegador (que só sai no toque em Ativar), "Agora não" que
 * espera 7 dias, métrica por momento e o cupom da loja entregue na hora.
 */
export function usePushInvite({
  slug,
  merchantId,
  channel: mainChannel,
  snoozedChannel,
  visible,
  viewKey,
}: Options) {
  const { user } = useUser();
  const userId = user.get()?.id ?? null;
  const [state, setState] = useState<PushState>(() => pushState());
  const [subscribed, setSubscribed] = useState(() => !!slug && isSubscribedToStore(slug));
  const [snoozed, setSnoozed] = useState(() => isPushSnoozed());
  /** Já estava em "Agora não" ao abrir a tela (quem dispensou agora não vê mais nada). */
  const [snoozedAtMount] = useState(() => isPushSnoozed());
  const channel = snoozedAtMount && snoozedChannel ? snoozedChannel : mainChannel;
  const [busy, setBusy] = useState(false);
  /** undefined = ainda não ativou; null = ativou e a loja não tem cupom. */
  const [granted, setGranted] = useState<OptInGranted | null | undefined>(undefined);

  useEffect(() => {
    setState(pushState());
    setSubscribed(!!slug && isSubscribedToStore(slug));
  }, [slug]);

  /** Dá para convidar: navegador com push, ainda não ativou nesta loja e não bloqueou. */
  const canAsk =
    !!slug &&
    !subscribed &&
    (state === 'default' || state === 'granted' || state === 'needs-home-screen');

  // "Tem cupom te esperando" só quando a loja tem cupom ativo.
  const offerQuery = useQuery({
    queryKey: ['push-optin-offer', slug],
    queryFn: () => getOptInOffer(slug),
    enabled: !!slug && visible && canAsk,
    staleTime: 5 * 60 * 1000,
  });

  const track = (action: PromptAction, reason?: string) =>
    recordPromptEvent(slug, {
      channel,
      action,
      reason,
      device: deviceId(),
      platform: platform(),
      standalone: isStandalone(),
    });

  useEffect(() => {
    if (visible && canAsk && firstPromptView(`${channel}:${slug}:${viewKey ?? ''}`)) track('shown');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, canAsk, channel, slug, viewKey]);

  const snooze = () => {
    snoozePush();
    setSnoozed(true);
    track('dismissed');
  };

  const activate = async () => {
    if (!slug || busy) return;
    setBusy(true);
    try {
      let id: string | null;
      try {
        id = await enablePush(userId, merchantId);
      } catch (e) {
        track('error', pushErrorReason(e));
        Sentry.captureException(e, { tags: { area: 'push', channel } });
        enhancedToast.error('Não foi possível ativar agora', {
          description: 'Recarregue a página e tente de novo.',
        });
        return;
      }
      if (!id) {
        const now = pushState();
        setState(now);
        if (now === 'denied') {
          track('denied');
        } else {
          snoozePush();
          setSnoozed(true);
          track('dismissed');
        }
        enhancedToast.info('Avisos não ativados', {
          description: 'Você pode ativar depois por aqui mesmo.',
        });
        return;
      }

      const reward = await registerPushSubscription(slug, {
        subscription_id: id,
        platform: platform(),
        standalone: isStandalone(),
      }).catch(e => {
        Sentry.captureException(e, { tags: { area: 'push', channel } });
        return null;
      });
      markSubscribedToStore(slug);
      setSubscribed(true);
      setState(pushState());
      track('accepted');
      // O cupom já vai para o carrinho: o checkout aplica o cupom pendente sozinho.
      if (reward?.code) savePendingCoupon(reward.code);
      setGranted(reward ?? null);
      enhancedToast.success('Avisos ativados', {
        description: reward?.code
          ? `Seu cupom ${reward.code} já está guardado para o próximo pedido.`
          : 'Pronto: a gente te avisa por aqui.',
      });
    } finally {
      setBusy(false);
    }
  };

  return {
    state,
    canAsk,
    snoozed,
    snoozedAtMount,
    busy,
    offer: offerQuery.data ?? null,
    granted,
    userId,
    activate,
    snooze,
    dismissGranted: () => setGranted(undefined),
  };
}

/**
 * PRAT-43 — aparelho já inscrito nesta loja: renova a inscrição na API (liga ao
 * cliente depois do login) e refaz o external_id e a tag da loja no OneSignal.
 */
export function usePushSync(slug?: string, merchantId?: string | null) {
  const { user } = useUser();
  const userId = user.get()?.id ?? null;

  useEffect(() => {
    if (!slug || !pushEnabled() || !isSubscribedToStore(slug)) return;
    let alive = true;
    currentSubscriptionId().then(id => {
      if (!alive || !id) return;
      registerPushSubscription(slug, {
        subscription_id: id,
        platform: platform(),
        standalone: isStandalone(),
      }).catch(() => undefined);
      syncPushIdentity(userId, merchantId);
    });
    return () => {
      alive = false;
    };
  }, [slug, merchantId, userId]);
}
